import type { ReactElement } from "react"
import type { Control, FieldPath, FieldValues } from "react-hook-form"
import { useController } from "react-hook-form"

import { AerosRadioGroup, type AerosRadioGroupProps } from "../AerosRadioGroup"

interface FormRadioGroupProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>, TValue>
  extends Omit<AerosRadioGroupProps<TValue>, "name" | "defaultValue" | "onChange"> {
  control: Control<TFieldValues>
  name: TName
  onChange?: (value: TValue) => void
}

export const FormRadioGroup = <TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>, TValue>({
  control,
  name,
  helperText,
  onChange,
  ...props
}: FormRadioGroupProps<TFieldValues, TName, TValue>): ReactElement => {
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
  })

  const handleChange = (value: TValue) => {
    field.onChange(value)
    onChange?.(value)
  }

  return (
    <AerosRadioGroup
      {...field}
      {...props}
      error={!!error}
      helperText={error?.message ?? helperText}
      onChange={handleChange}
    />
  )
}
